<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
 columns: { type: Array, required: true },
 // [{ key: 'name', label: 'Nome', format: 'text', align: 'left' }]
 rows: { type: Array, default: () => [] },
 sortable: { type: Boolean, default: true },
 emptyText: { type: String, default: 'Sem dados' },
 exportable: { type: Boolean, default: true },
 exportFilename: { type: String, default: 'relatorio' },
});

const sortKey = ref('');
const sortDir = ref('desc');

const toggleSort = key => {
 if (!props.sortable) return;
 if (sortKey.value === key) {
 sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
 } else {
 sortKey.value = key;
 sortDir.value = 'desc';
 }
};

const sortedRows = computed(() => {
 if (!sortKey.value || !props.sortable) return props.rows;
 return [...props.rows].sort((a, b) => {
 const av = a[sortKey.value];
 const bv = b[sortKey.value];
 const cmp = typeof av === 'string' ? av.localeCompare(bv) : (av || 0) - (bv || 0);
 return sortDir.value === 'asc' ? cmp : -cmp;
 });
});

const exportCSV = () => {
 if (!props.rows.length) return;
 const header = props.columns.map(c => c.label).join(';');
 const body = sortedRows.value.map(row =>
 props.columns.map(col => {
 const val = row[col.key];
 if (val == null) return '';
 if (col.format === 'currency') return Number(val).toFixed(2);
 if (col.format === 'percent') return `${Number(val).toFixed(1)}%`;
 return String(val).replace(/;/g, ',');
 }).join(';')
 ).join('\n');
 const bom = '\uFEFF';
 const blob = new Blob([bom + header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
 const url = URL.createObjectURL(blob);
 const a = document.createElement('a');
 a.href = url;
 a.download = `${props.exportFilename}_${new Date().toISOString().slice(0, 10)}.csv`;
 a.click();
 URL.revokeObjectURL(url);
};

const formatCell = (value, format) => {
 if (value == null) return '-';
 switch (format) {
 case 'currency':
 return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
 case 'percent':
 return `${Number(value).toFixed(1)}%`;
 case 'time': {
 const h = Number(value);
 if (h <= 0) return '-';
 if (h >= 24) return `${Math.floor(h / 24)}d ${Math.round(h % 24)}h`;
 if (h >= 1) return `${Math.floor(h)}h ${Math.round((h % 1) * 60)}m`;
 return `${Math.round(h * 60)}m`;
 }
 case 'number':
 return Number(value).toLocaleString('pt-BR');
 default:
 return value;
 }
};
</script>

<template>
 <div class="overflow-x-auto">
 <div v-if="exportable && rows.length > 0" class="flex justify-end mb-2">
 <button
 class="flex items-center gap-1 text-[11px] font-medium text-n-slate-9 hover:text-n-slate-11 dark:hover:text-n-slate-7 transition-colors"
 @click="exportCSV"
 >
 <span class="i-lucide-download size-3.5" />
 Exportar CSV
 </button>
 </div>
 <table class="w-full text-sm">
 <thead>
 <tr class="border-b border-n-weak">
 <th
 v-for="col in columns"
 :key="col.key"
 class="py-2 px-3 text-xs font-medium text-n-slate-10 uppercase tracking-wider"
 :class="[
 col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left',
 sortable ? 'cursor-pointer select-none hover:text-n-slate-11' : '',
 ]"
 @click="toggleSort(col.key)"
 >
 <span class="inline-flex items-center gap-1">
 {{ col.label }}
 <span
 v-if="sortable && sortKey === col.key"
 :class="sortDir === 'asc' ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
 class="size-3"
 />
 </span>
 </th>
 </tr>
 </thead>
 <tbody>
 <tr
 v-for="(row, i) in sortedRows"
 :key="i"
 class="border-b border-n-weak hover:bg-n-alpha-1/30"
 >
 <td
 v-for="col in columns"
 :key="col.key"
 class="py-2 px-3 text-n-slate-12"
 :class="col.align === 'right' ? 'text-right tabular-nums' : col.align === 'center' ? 'text-center' : 'text-left'"
 >
 <slot :name="col.key" :row="row" :value="row[col.key]">
 {{ formatCell(row[col.key], col.format) }}
 </slot>
 </td>
 </tr>
 <tr v-if="rows.length === 0">
 <td :colspan="columns.length" class="py-8 text-center text-n-slate-9">
 {{ emptyText }}
 </td>
 </tr>
 </tbody>
 </table>
 </div>
</template>
